import { useMemo, useState } from 'react';
import { App } from 'antd';
import type { TableProps } from 'antd';
import {
  ApartmentOutlined,
  BankOutlined,
  ClusterOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';

import { DataTable, MenuThaoTac, PageHeader, StatTile } from '../../../components/shared';
import { Badge, Button, Empty, Input, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { DonViFormDialog } from '../components/DonViFormDialog';
import { LOAI_DON_VI, NHAN_LOAI_DON_VI, TONG_LOAI_DON_VI, tongDiem } from '../constants';
import { useQuyenVanBan } from '../hooks/useQuyenVanBan';
import { useDonVi, useLuuDonVi, useXoaDonVi } from '../hooks/useVanBan';
import type { DonVi, LoaiDonVi } from '../types';

/** Danh sách, vai trò các cấp (`/xu-ly-van-ban/cau-hinh/vai-tro-cac-cap`), chuyển từ foxainative. */
export function VaiTroCacCapView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const { modal } = App.useApp();
  const { data, isLoading, error, refetch } = useDonVi();
  const luu = useLuuDonVi();
  const xoa = useXoaDonVi();

  const { canCreate, canUpdate, canDelete } = useQuyenVanBan('processing-unit');

  const [tuKhoa, setTuKhoa] = useState('');
  const [loai, setLoai] = useState<LoaiDonVi | undefined>();
  const [moForm, setMoForm] = useState(false);
  const [dangSua, setDangSua] = useState<DonVi | null>(null);

  const tatCa = useMemo(
    () => [...(data?.data ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [data],
  );
  const rows = useMemo(() => {
    const q = tuKhoa.trim().toLowerCase();
    return tatCa.filter(
      (r) =>
        (!q || r.name.toLowerCase().includes(q) || r.functions.toLowerCase().includes(q)) &&
        (!loai || r.type === loai),
    );
  }, [tatCa, tuKhoa, loai]);
  const coLoc = Boolean(tuKhoa.trim() || loai);
  const thongKe = data?.stats;

  const moThem = () => {
    luu.reset();
    setDangSua(null);
    setMoForm(true);
  };
  const moSua = (r: DonVi) => {
    luu.reset();
    setDangSua(r);
    setMoForm(true);
  };
  const hoiXoa = (r: DonVi) => {
    const tenLoai = t(NHAN_LOAI_DON_VI[r.type]).toLowerCase();
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: t('vanBanDonVi.xacNhanXoa', { loai: tenLoai, ten: r.name }),
      content: t('vanBanDonVi.xacNhanXoaMo'),
      okText: t('vanBanDonVi.nutXoa', { loai: tenLoai }),
      okButtonProps: { danger: true },
      cancelText: t('chung.huy'),
      onOk: () => xoa.mutateAsync(r.id),
    });
  };

  const columns: TableProps<DonVi>['columns'] = [
    {
      title: t('chung.stt'),
      width: 64,
      render: (_v, _r, i) => <span className="text-xs tabular-nums text-content-muted">{i + 1}</span>,
    },
    {
      title: t('vanBanDonVi.cot.ten'),
      dataIndex: 'name',
      width: 170,
      render: (name: string) => <span className="font-medium text-content-primary">{name}</span>,
    },
    {
      title: t('vanBanDonVi.cot.loai'),
      dataIndex: 'type',
      width: 110,
      render: (type: LoaiDonVi) => <Badge variant={TONG_LOAI_DON_VI[type]} className="whitespace-nowrap">{t(NHAN_LOAI_DON_VI[type])}</Badge>,
    },
    {
      title: t('vanBanDonVi.cot.nhiemVu'),
      dataIndex: 'functions',
      render: (f: string) => (
        // Chặn bề rộng để xuống dòng: bảng cuộn ngang theo nội dung (DataTable, x: max-content)
        <span className="block max-w-60 text-xs leading-relaxed text-content-secondary">{f || t('chung.khongCo')}</span>
      ),
    },
    {
      title: t('vanBanDonVi.cot.phuTrach'),
      width: 140,
      render: (_v, r) => (
        <span className="text-xs text-content-secondary">
          {r.type === 'CA_NHAN' || !r.supervisor ? t('chung.khongCo') : r.supervisor}
        </span>
      ),
    },
    {
      title: t('vanBanDonVi.cot.trucThuoc'),
      dataIndex: 'parent_name',
      width: 140,
      render: (p: string | null) => <span className="text-xs text-content-secondary">{p || t('chung.khongCo')}</span>,
    },
    {
      title: t('vanBanDonVi.cot.diem'),
      dataIndex: 'score',
      width: 100,
      align: 'center',
      render: (s: number | null) =>
        s != null ? (
          <Badge variant={tongDiem(s)} className="tabular-nums">
            {s.toLocaleString(locale, {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}
          </Badge>
        ) : (
          <span className="text-xs text-content-muted">{t('chung.khongCo')}</span>
        ),
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      render: (_v, r) => (
        <MenuThaoTac
          tenBanGhi={r.name}
          muc={[
            {
              khoa: 'sua',
              nhan: t('vanBanDonVi.sua'),
              icon: <EditOutlined className="text-primary-600" />,
              hien: canUpdate,
              nhom: 'sua',
              onChon: () => moSua(r),
            },
            {
              khoa: 'xoa',
              nhan: t('vanBanDonVi.xoa'),
              icon: <DeleteOutlined />,
              hien: canDelete,
              nhom: 'pha-huy',
              onChon: () => hoiXoa(r),
            },
          ]}
        />
      ),
    },
  ];

  const so = (n: number | undefined) => soVN(n ?? 0, locale);

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[
            { label: t('nav.nhom.xuLyVanBan') },
            { label: t('nav.muc.cauHinhXuLyVanBan') },
            { label: t('vanBanDonVi.tieuDe') },
          ]}
          titleIcon={<ApartmentOutlined className="text-primary-600" />}
          title={t('vanBanDonVi.tieuDe')}
          subtitle={t('vanBanDonVi.moTa')}
          actions={
            <>
              <Button
                variant="ghost"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => void refetch()}
              >
                <ReloadOutlined className={isLoading ? 'animate-spin' : ''} />
              </Button>
              {canCreate && (
                <Button variant="primary" icon={<PlusOutlined />} onClick={moThem}>
                  {t('vanBanChung.themMoi')}
                </Button>
              )}
            </>
          }
        />

        <div className="grid grid-cols-12 gap-4">
          <StatTile span="col-span-12 sm:col-span-6 lg:col-span-3" nhan={t('vanBanDonVi.thongKe.tong')} so={so(thongKe?.total)} icon={<ApartmentOutlined />} tone="primary" />
          <StatTile span="col-span-12 sm:col-span-6 lg:col-span-3" nhan={t('vanBanDonVi.thongKe.coQuan')} so={so(thongKe?.co_quan)} icon={<BankOutlined />} tone="violet" />
          <StatTile span="col-span-12 sm:col-span-6 lg:col-span-3" nhan={t('vanBanDonVi.thongKe.phongBan')} so={so(thongKe?.phong_ban)} icon={<ClusterOutlined />} tone="teal" />
          <StatTile span="col-span-12 sm:col-span-6 lg:col-span-3" nhan={t('vanBanDonVi.thongKe.caNhan')} so={so(thongKe?.ca_nhan)} icon={<UserOutlined />} tone="warning" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Input
            className="min-w-[200px] max-w-sm flex-1"
            allowClear
            prefix={<SearchOutlined className="text-primary-500" />}
            placeholder={t('vanBanDonVi.timTheo')}
            aria-label={t('vanBanDonVi.timTheo')}
            value={tuKhoa}
            onChange={(e) => setTuKhoa(e.target.value)}
          />
          <Select
            allowClear
            className="min-w-[10rem]"
            placeholder={t('vanBanDonVi.tatCaLoai')}
            aria-label={t('vanBanDonVi.locLoai')}
            options={LOAI_DON_VI.map((value) => ({ value, label: t(NHAN_LOAI_DON_VI[value]) }))}
            value={loai}
            onChange={(v) => setLoai(v as LoaiDonVi | undefined)}
          />
        </div>

        <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          {error ? (
            <div role="alert" className="flex items-center justify-between gap-3 px-4 py-3">
              <span className="text-sm text-danger-700">{t('vanBanDonVi.loiDanhSach', { loi: dichLoi(error) })}</span>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          ) : (
            <DataTable<DonVi>
              caption={t('vanBanDonVi.bangCaption')}
              rowKey="id"
              dataSource={rows}
              columns={columns}
              loading={isLoading}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              locale={{
                emptyText: isLoading ? (
                  ' '
                ) : (
                  <Empty
                    icon={ApartmentOutlined}
                    title={t(coLoc ? 'vanBanDonVi.rongLoc' : 'vanBanDonVi.rongThat')}
                    description={coLoc ? undefined : t('vanBanDonVi.rongThatMo')}
                    action={
                      coLoc || !canCreate ? undefined : (
                        <Button variant="primary" icon={<PlusOutlined />} onClick={moThem}>
                          {t('vanBanChung.themMoi')}
                        </Button>
                      )
                    }
                  />
                ),
              }}
            />
          )}
        </div>
      </div>

      <DonViFormDialog
        open={moForm}
        donVi={dangSua}
        tatCa={tatCa}
        saving={luu.isPending}
        loiMayChu={luu.error}
        onClose={() => setMoForm(false)}
        onSubmit={(vao) => luu.mutate({ id: dangSua?.id ?? null, vao }, { onSuccess: () => setMoForm(false) })}
      />
    </div>
  );
}
